// Iframe preview builder — VFS shim (learn-code-studio-prd.md §5).
//
// PRD §5.1 specifies a sandboxed `<iframe sandbox="allow-scripts">` (no
// allow-same-origin) loading a single `srcdoc` built from the VFS, with
// external `<img src="images/cat.png">` resolved via a service worker that
// serves cached VFS reads.
//
// V0 reality: a `srcdoc` iframe WITHOUT `allow-same-origin` runs at an opaque
// origin and cannot be controlled by a service worker scoped to our app origin,
// so the SW asset-serving path can't apply here (PRD §5.1 notes this is the
// deep part). Instead we run the VFS shim in the PARENT: before injecting the
// document we rewrite any `src`/`href` that points at a VFS asset to the
// asset's inlined data: URL. This serves the exact same purpose — kid assets
// load, nothing external leaks — while keeping the strict sandbox.
//
// The true SW-based cross-fetch shim is deferred to the dedicated preview
// origin in PRD §5.2 (V1). This file is the V0 substitute and is flagged
// PARTIAL for the SW piece.

import type { VfsFile } from './codeApi';

/**
 * Browser-extension noise guard — MUST be the first script in every preview /
 * game srcdoc. Wallet extensions (Coinbase, MetaMask, …) inject provider
 * scripts into every frame; inside our deliberately opaque-origin sandbox
 * (no \`allow-same-origin\`) their first \`localStorage\` touch throws an
 * uncaught SecurityError that scares anyone with DevTools open. Errors whose
 * source is an extension URL are cancelled before the browser logs them and
 * never reach the console relay. Page-world only: an extension's ISOLATED
 * content-script errors are outside any page's reach (browser-level noise).
 * NEVER "fix" this by adding allow-same-origin — that flag is load-bearing
 * (the frame must not reach the app origin's storage/cookies/token).
 */
export const EXTENSION_NOISE_GUARD = `
<script>
(function () {
  var isExtensionSource = function (src) {
    return /^(chrome|moz|safari-web|ms-browser)-extension:/.test(src || '');
  };
  window.addEventListener('error', function (e) {
    if (isExtensionSource(e.filename)) {
      e.preventDefault();          // no "Uncaught …" console entry
      e.stopImmediatePropagation(); // never reaches the console relay below
    }
  }, true);
})();
</script>`;

/**
 * Web Storage stand-in for the opaque-origin sandbox. Without
 * \`allow-same-origin\`, the first read of \`localStorage\` / \`sessionStorage\`
 * throws "SecurityError: … The document is sandboxed and lacks the
 * 'allow-same-origin' flag". AI-written games and sites use storage all the
 * time (high scores, saved settings, to-do lists), and the backend semantic
 * check accepts both globals, so that code crashed at run time (prod report
 * 2026-10-01). Install an in-memory Storage when the real one throws: the code
 * runs, and values last for this run only (a re-run starts empty). Supports
 * the Storage methods plus property access (\`localStorage.best = 3\`).
 * Must precede every engine and kid script. NEVER "fix" storage by adding
 * allow-same-origin (see EXTENSION_NOISE_GUARD).
 */
export const SANDBOX_STORAGE_SHIM = `
<script>
(function () {
  var makeStorage = function () {
    var data = new Map();
    var api = {
      getItem: function (k) { k = String(k); return data.has(k) ? data.get(k) : null; },
      setItem: function (k, v) { data.set(String(k), String(v)); },
      removeItem: function (k) { data.delete(String(k)); },
      clear: function () { data.clear(); },
      key: function (i) { var keys = Array.from(data.keys()); return i >= 0 && i < keys.length ? keys[i] : null; }
    };
    return new Proxy(api, {
      get: function (t, p) {
        if (p === 'length') return data.size;
        if (typeof p === 'symbol' || p in t) return t[p];
        return data.has(p) ? data.get(p) : undefined;
      },
      set: function (t, p, v) {
        if (typeof p !== 'symbol' && !(p in t) && p !== 'length') data.set(p, String(v));
        return true;
      },
      has: function (t, p) { return p in t || data.has(p); },
      deleteProperty: function (t, p) { data.delete(String(p)); return true; },
      ownKeys: function () { return Array.from(data.keys()); },
      getOwnPropertyDescriptor: function (t, p) {
        return data.has(p)
          ? { value: data.get(p), writable: true, enumerable: true, configurable: true }
          : undefined;
      }
    });
  };
  ['localStorage', 'sessionStorage'].forEach(function (name) {
    try { if (window[name]) return; } catch (e) {}
    try {
      Object.defineProperty(window, name, { value: makeStorage(), configurable: true, enumerable: true });
    } catch (e) {}
  });
})();
</script>`;

/**
 * Max chars of an Error stack relayed on a console line (D-HARN-11a). Clipped
 * INSIDE the sandbox shim so an enormous stack never bloats the postMessage.
 */
export const STACK_CLIP_CHARS = 4_000;

/**
 * Console text for a MUTED error: the browser reports an error thrown inside a
 * cross-origin no-cors script (a vendored engine whose CORS load fell back, or
 * a script the page pulls from elsewhere) only as "Script error." — no message,
 * file, line or stack. Say what happened in words a kid can act on.
 */
export const MUTED_ERROR_TEXT =
  'The browser withheld this error’s message and stack because a script loaded without CORS. ' +
  'The engine host must allow anonymous CORS before AI can diagnose this crash.';

export const CONSOLE_CAPTURE = `
<script>
(function () {
  // Fix-turn evidence (D-HARN-11a): when an Error OBJECT is available (uncaught
  // window errors, Error args to console.error) its stack rides the console line
  // clipped — both manual and automatic fixes receive the original evidence.
  var clipStack = function (s) {
    s = String(s);
    var marker = '\\n[stack truncated]';
    return s.length > ${STACK_CLIP_CHARS}
      ? s.slice(0, ${STACK_CLIP_CHARS} - marker.length) + marker : s;
  };
  // Console styling: \`console.log('%c Hi', 'color: red')\` — each %c consumes
  // one CSS argument. DevTools renders it; the relay is plain text, so drop the
  // tokens and their CSS (Phaser's start-up banner is a data:-URL logo in CSS).
  var plainText = function (args) {
    var list = Array.prototype.slice.call(args);
    if (typeof list[0] === 'string' && list[0].indexOf('%c') !== -1) {
      var parts = list[0].split('%c');
      list = [parts.join('').trim()].concat(list.slice(parts.length));
    }
    return list.map(function (value) {
      // Error-like rejection objects need their message too; String(object)
      // alone hides it behind [object Object].
      if (value && typeof value === 'object' && typeof value.message === 'string') {
        return (typeof value.name === 'string' && value.name ? value.name + ': ' : '') + value.message;
      }
      return String(value);
    }).join(' ');
  };
  var send = function (level, args) {
    try {
      var msg = {
        __airbotixConsole: true,
        level: level,
        text: plainText(args)
      };
      if (level === 'error') {
        for (var i = 0; i < args.length; i++) {
          var a = args[i];
          if (a && typeof a === 'object' && a.stack) { msg.stack = clipStack(a.stack); break; }
        }
      }
      parent.postMessage(msg, '*');
    } catch (e) {}
  };
  ['log', 'info', 'warn', 'error'].forEach(function (level) {
    var orig = console[level];
    console[level] = function () { send(level, arguments); orig && orig.apply(console, arguments); };
  });
  // Uncaught errors carry a location — post it structured so the console can show
  // file:line and the editor can jump there (sourceURL makes filename = the file).
  window.addEventListener('error', function (e) {
    if (/^(chrome|moz|safari-web|ms-browser)-extension:/.test(e.filename || '')) return;
    // A muted cross-origin error: no Error object, no file, only "Script error.".
    var muted = !e.error && !e.filename && /^Script error\\.?$/.test(e.message || '');
    try {
      parent.postMessage({
        __airbotixConsole: true,
        level: 'error',
        text: e.message || String(e.error || 'Error'),
        hint: muted ? ${JSON.stringify(MUTED_ERROR_TEXT)} : undefined,
        muted: muted || undefined,
        loc: e.filename ? { file: e.filename, line: e.lineno || 0, col: e.colno || 0 } : undefined,
        stack: e.error && e.error.stack ? clipStack(e.error.stack) : undefined
      }, '*');
    } catch (err) {}
  });
  window.addEventListener('unhandledrejection', function (e) {
    send('error', ['Unhandled promise:', e.reason]);
  });
  parent.postMessage({ __airbotixConsole: true, level: 'info', text: 'ready' }, '*');
})();
</script>`;

// Keep in sync with `BINARY_ASSET_MIME` (codeApi.ts) — the API-boundary wrapper for
// authed studio reads. This map is the LAST line of defence: it types raw-base64
// asset content that reaches a srcdoc builder unwrapped (the public /play snapshot
// fetch bypasses `toStudioContent`). Only intended difference: `svg` (text kind).
export const ASSET_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  webp: 'image/webp',
  // Audio MUST carry a real audio/* MIME: data: URLs are never MIME-sniffed, so a
  // raw-base64 snapshot asset (e.g. the public /play page) inlined as
  // application/octet-stream is refused by <audio>/new Audio() — BGM silently dies.
  wav: 'audio/wav',
  mp3: 'audio/mpeg',
  ogg: 'audio/ogg',
  m4a: 'audio/mp4',
  mp4: 'video/mp4',
  webm: 'video/webm',
  // Non-executable DATA assets (game data, fonts, shaders) — inlined as data: URLs
  // for the loader (Phaser load.json/atlas/bitmapFont accept data: URLs directly).
  json: 'application/json',
  xml: 'application/xml',
  csv: 'text/csv',
  ttf: 'font/ttf',
  otf: 'font/otf',
  woff: 'font/woff',
  woff2: 'font/woff2',
  fnt: 'application/octet-stream',
  glsl: 'text/plain',
  frag: 'text/plain',
  vert: 'text/plain',
  atlas: 'text/plain',
  // 3D model (binary glTF) — THREE.GLTFLoader's FileLoader accepts data: URLs.
  glb: 'model/gltf-binary',
};

function file(files: VfsFile[], path: string): string {
  return files.find((f) => f.path === path)?.content ?? '';
}

/**
 * Rewrite VFS-relative asset references to inlined data URLs (the parent-side
 * VFS shim). An asset's `content` is already a data: URL when binary; for the
 * fallback path assets are rare, so this is mostly a guard for future uploads.
 */
function inlineAssets(html: string, files: VfsFile[]): string {
  const assets = files.filter((f) => f.kind === 'asset');
  if (assets.length === 0) return html;
  let out = html;
  for (const a of assets) {
    const ext = a.path.split('.').pop()?.toLowerCase() ?? '';
    const dataUrl = a.content.startsWith('data:')
      ? a.content
      : `data:${ASSET_MIME[ext] ?? 'application/octet-stream'};base64,${a.content}`;
    // Replace src="path" / src='path' / src=path and href variants.
    const escaped = a.path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(new RegExp(`(src|href)=("|')${escaped}\\2`, 'g'), `$1=$2${dataUrl}$2`);
  }
  return out;
}

/** Build the full sandboxed srcdoc from the VFS. */
export function buildSrcDoc(files: VfsFile[]): string {
  const html = inlineAssets(file(files, 'index.html'), files);
  const css = file(files, 'style.css');
  const js = file(files, 'script.js');
  return [
    '<!doctype html>',
    '<html><head><meta charset="utf-8">',
    EXTENSION_NOISE_GUARD,
    SANDBOX_STORAGE_SHIM,
    `<style>${css}</style>`,
    '</head><body>',
    CONSOLE_CAPTURE,
    html,
    `<script>${js}${'<'}/script>`,
    '</body></html>',
  ].join('\n');
}

export interface ConsoleLine {
  level: 'log' | 'info' | 'warn' | 'error';
  text: string;
  /** Source location of an uncaught error (sourceURL → file). Used to jump to it. */
  loc?: { file: string; line: number; col: number };
  /**
   * The Error object's stack, clipped to STACK_CLIP_CHARS in the shim
   * Used by the visible console, manual fix prompt and bounded RunReport details.
   */
  stack?: string;
  /**
   * True when the browser muted the error ("Script error." from a cross-origin
   * no-cors script): `text` stays raw and no location or stack
   * exists. The host logs it so the hidden crash is visible to diagnostics.
   */
  muted?: boolean;
  /** Explanation of missing evidence, separate from the original error text. */
  hint?: string;
}

export function isConsoleMessage(data: unknown): data is { __airbotixConsole: true } & ConsoleLine {
  return (
    typeof data === 'object' &&
    data !== null &&
    '__airbotixConsole' in data &&
    (data as { __airbotixConsole: unknown }).__airbotixConsole === true
  );
}
